/**
 * Confirmation for a tag library delete — deliberately lighter than the
 * type-to-confirm DeleteConfirmModal used for lessons/courses (per design):
 * a tag delete is reversible in spirit (the tag can be recreated), it's the
 * unlinking from live content that can't be undone, so the warning box
 * spells out exactly what that means instead of gating the button on typed
 * confirmation text.
 */
import type { CSSProperties } from 'react';
import { adminButton, adminStatusStyle, adminText, adminTokens } from '../admin-ui';
import { ModalShell } from './ModalShell';

export interface DeleteTagModalProps {
  isOpen: boolean;
  tagName: string;
  lessonCount: number;
  courseCount: number;
  isDeleting?: boolean;
  errorMessage?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function describeImpact(lessonCount: number, courseCount: number): string {
  const lessonPhrase = lessonCount > 0 ? `${lessonCount} lesson${lessonCount === 1 ? '' : 's'}` : '';
  const coursePhrase = courseCount > 0 ? `${courseCount} course${courseCount === 1 ? '' : 's'}` : '';

  if (!lessonPhrase && !coursePhrase) {
    return "This tag isn't used by any lessons or courses. This can't be undone.";
  }

  const usagePhrase = lessonPhrase && coursePhrase ? `${lessonPhrase} and ${coursePhrase}` : lessonPhrase || coursePhrase;
  return `This removes it from ${usagePhrase}. Those items keep their other tags. This can't be undone.`;
}

export function DeleteTagModal({
  isOpen,
  tagName,
  lessonCount,
  courseCount,
  isDeleting = false,
  errorMessage = null,
  onConfirm,
  onCancel,
}: DeleteTagModalProps) {
  const titleId = 'delete-tag-modal-title';

  return (
    <ModalShell isOpen={isOpen} onClose={onCancel} ariaLabelledBy={titleId} width="480px" presentation="admin">
      <div style={styles.body}>
        <h3 id={titleId} style={styles.title}>Delete tag</h3>
        <p style={styles.text}>
          <span style={styles.textMuted}>Delete </span>
          <strong>{tagName}</strong>
          <span style={styles.textMuted}> from the tag library?</span>
        </p>
        <div style={styles.notice}>{describeImpact(lessonCount, courseCount)}</div>
        {errorMessage && (
          <p role="alert" style={styles.error}>{errorMessage}</p>
        )}
        <div style={styles.actions}>
          <button
            type="button"
            onClick={onCancel}
            style={{ ...styles.cancelButton, ...(isDeleting ? styles.buttonDisabled : {}) }}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{ ...styles.confirmButton, ...(isDeleting ? styles.buttonDisabled : {}) }}
            disabled={isDeleting}
            aria-busy={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete tag'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// Opened only from the Tags page, so it uses the admin frame and recipes.
const styles: Record<string, CSSProperties> = {
  body: {
    padding: adminTokens.spacing.xl,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    ...adminText.sectionTitle,
    margin: `0 0 ${adminTokens.spacing.md} 0`,
  },
  text: {
    margin: `0 0 ${adminTokens.spacing.lg} 0`,
    fontSize: adminTokens.typography.body.size,
    color: adminTokens.text.primary,
    lineHeight: 1.5,
  },
  // The tag name is the only strongly-colored word in this sentence — the
  // surrounding "Delete"/"from the tag library?" stay secondary so the name
  // is what the eye lands on.
  textMuted: {
    color: adminTokens.text.secondary,
  },
  notice: {
    ...adminStatusStyle('error'),
    margin: `0 0 ${adminTokens.spacing.lg} 0`,
    padding: '12px 16px',
  },
  error: {
    margin: `0 0 ${adminTokens.spacing.lg} 0`,
    fontSize: adminTokens.typography.body.size,
    color: adminTokens.status.error.text,
    lineHeight: 1.5,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: adminTokens.spacing.md,
  },
  cancelButton: adminButton.secondary,
  confirmButton: adminButton.danger,
  buttonDisabled: adminButton.disabled,
};
